'use client';

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import { useParams, usePathname, useRouter } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { currencyFormatter } from '@web/utils/currency';
import { FlexBox } from '@web/components/flex-box';
import fetcher from '@web/utils/fetch';
import useSWRImmutable from 'swr/immutable';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import NextImage from 'next/image';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useCollections } from '@web/context/collections';
import DeleteIcon from '@mui/icons-material/Delete';

interface NotificationType {
  notification_id?: number;
  sellers: number[];
  target_price: number;
  is_recurring: boolean;
}

interface NotificationRequest {
  user_id: string;
  pid: number;
  email: string;
  date_when_subscribed: string;
  price_when_subscribed: number;
  sellers: number[] | null;
  target_price: number;
  is_recurring: boolean;
  notification_id?: number;
}

const Notification = ({ product = {}, isMobile: propIsMobile }: any) => {
  const router = useRouter();
  const { user, isLoading } = useUser();
  const { notifications } = useCollections();
  const pathname = usePathname();
  const params = useParams();
  const [filteredOffers, setFilteredOffers] = useState<any>([]);
  const [notified, setNotified] = useState(false);
  const [open, setOpen] = useState(false);
  const [sellers, setSellers] = useState<number[] | null>(null);
  const [targetPrice, setTargetPrice] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = propIsMobile || isMobileScreen;
  const [notification, setNotification] = useState<NotificationType | null>(
    null
  );

  const { data: productWithOffers } = useSWRImmutable(
    params?.slug && open && `/api/products/${params?.slug}`,
    fetcher
  );

  useEffect(() => {
    if (!productWithOffers) return;
    const filtered = productWithOffers.offers
      ?.filter((offer) => !offer.seller_name.toLowerCase().includes('amazon'))
      .filter((offer) => {
        if (productWithOffers.offers.length !== 1 && offer.seller_id === 156)
          return false;
        return true;
      });

    setFilteredOffers(filtered);
  }, [productWithOffers]);

  useEffect(() => {
    if (product.id && user && notifications) {
      const notification = notifications.find((n) => n.pid === product.id);
      if (!notification) return;
      setNotified(true);
      setNotification(notification);
      setSellers(notification.sellers);
      setTargetPrice(notification.target_price);
      setIsRecurring(notification.is_recurring);
    }
  }, [product.id, user, notifications]);

  const handleButtonClick = async () => {
    if (!user) {
      await router.push(`/api/auth/login?returnTo=${pathname}`);
      return;
    }

    setOpen(true);
  };

  const handleSubmit = async () => {
    setOpen(false);
    if (!user?.sub || !user?.email) return;
    const body: NotificationRequest = {
      user_id: user?.sub,
      pid: product.id,
      email: user?.email,
      date_when_subscribed: productWithOffers.last_price_updated_at,
      price_when_subscribed: productWithOffers?.offers[0].base_price,
      sellers,
      target_price: Math.round(Number(targetPrice) * 100) / 100,
      is_recurring: isRecurring,
    };

    if (notification) {
      body.notification_id = notification.notification_id;
    }

    const response = await fetch('/api/notifications', {
      method: notification ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const data = await response.json();
      setNotification(data);
      setSuccess(true);
      setNotified(true);
      setTimeout(() => setSuccess(false), 6000);
    }
  };

  const handleDelete = async () => {
    if (!notification?.notification_id) return;

    const response = await fetch(
      `/api/notifications/${notification?.notification_id}`,
      {
        method: 'DELETE',
      }
    );

    if (response.ok) {
      setOpen(false);
      setNotified(false);
      setNotification(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 6000);
    }
  };

  if (isLoading || !notifications) {
    return <></>;
  }
  return (
    <>
      {isMobile ? (
        <Card sx={{ my: 2 }}>
          {(notified !== undefined || !user) && (
            <Button
              variant={notified ? 'outlined' : 'contained'}
              color="primary"
              fullWidth
              endIcon={
                notified ? <NotificationsActiveIcon /> : <NotificationAddIcon />
              }
              onClick={handleButtonClick}
            >
              {notified ? 'Edit Price Alert' : 'Notify me when price drops'}
            </Button>
          )}
        </Card>
      ) : (
        <Box>
          <Button
            variant={notified ? 'outlined' : 'contained'}
            color="primary"
            endIcon={
              notified ? <NotificationsActiveIcon /> : <NotificationAddIcon />
            }
            onClick={handleButtonClick}
          >
            {notified ? 'Edit Price Alert' : 'Notify me when price drops'}
          </Button>
        </Box>
      )}
      <Dialog
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <FlexBox alignItems="center" gap={1}>
            <NotificationAddIcon color="primary" />
            <Typography variant="h6">
              {notified ? 'Edit price alert' : 'Set price alert'}
            </Typography>
          </FlexBox>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Box>
            <FlexBox gap={2} alignItems="center">
              <Box
                sx={{
                  width: 60,
                  height: 40,
                  position: 'relative',
                  borderRadius: 1,
                  overflow: 'hidden',
                  flexShrink: 0,
                  bgcolor: 'background.default',
                }}
              >
                <NextImage
                  src={product.image || '/images/placeholder.png'}
                  fill
                  sizes="small"
                  style={{ objectFit: 'contain' }}
                  alt={product.title || ''}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  {product.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current price range:{' '}
                  <Typography
                    component="span"
                    fontWeight={500}
                    color="text.primary"
                  >
                    {currencyFormatter.format(
                      product.lowest_price ||
                        product.product_status?.lowest_price
                    )}{' '}
                    -{' '}
                    {currencyFormatter.format(
                      product.highest_price ||
                        product.product_status?.highest_price
                    )}
                  </Typography>
                </Typography>
              </Box>
            </FlexBox>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Target price
            </Typography>
            <TextField
              size="medium"
              fullWidth
              variant="outlined"
              type="number"
              value={targetPrice}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setTargetPrice(value);
                }
              }}
              onBlur={() => {
                if (targetPrice) {
                  setTargetPrice(Number(targetPrice).toFixed(2));
                }
              }}
              inputProps={{
                step: '0.01',
                min: '0',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography color="text.secondary" fontWeight={500}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Select sellers to track
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={filteredOffers || []}
              value={
                sellers === null
                  ? filteredOffers || []
                  : filteredOffers?.filter((o) =>
                      sellers.includes(o.seller_id)
                    ) || []
              }
              getOptionLabel={(option) =>
                `${option.seller_name} - ${currencyFormatter.format(
                  option.base_price
                )}`
              }
              onChange={(_, newValue) => {
                if (newValue.length === filteredOffers?.length) {
                  setSellers(null);
                } else {
                  setSellers(newValue.map((v) => v.seller_id));
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.seller_name}>
                  <FlexBox gap={2} width="100%" pr={1}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        position: 'relative',
                        flexShrink: 0,
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                      }}
                    >
                      <NextImage
                        src={`https://img.logo.dev/${encodeURIComponent(
                          option.seller_url || ''
                        )}?token=pk_BDaBCUfDT2GGgSbZ8cuM0A&size=64`}
                        fill
                        sizes="small"
                        style={{ objectFit: 'contain' }}
                        alt={option.seller_name || ''}
                      />
                    </Box>
                    <FlexBox justifyContent="space-between" width="100%">
                      <Typography>{option.seller_name}</Typography>
                      <Typography color="text.secondary" fontWeight={500}>
                        {currencyFormatter.format(option.base_price)}
                      </Typography>
                    </FlexBox>
                  </FlexBox>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    sellers === null ? 'All sellers' : 'Select sellers'
                  }
                />
              )}
              renderTags={(selected, getTagProps) =>
                sellers === null ? (
                  <Chip
                    label="All sellers"
                    size="small"
                    icon={<AllInclusiveIcon sx={{ fontSize: 16 }} />}
                  />
                ) : (
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.seller_id}
                      label={option.seller_name}
                      size="small"
                      avatar={
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            position: 'relative',
                            borderRadius: '50%',
                            overflow: 'hidden',
                            bgcolor: 'background.default',
                          }}
                        >
                          <NextImage
                            src={`https://img.logo.dev/${encodeURIComponent(
                              option.seller_url || ''
                            )}?token=pk_BDaBCUfDT2GGgSbZ8cuM0A&size=64`}
                            fill
                            sizes="small"
                            style={{ objectFit: 'contain' }}
                            alt={option.seller_name || ''}
                          />
                        </Box>
                      }
                    />
                  ))
                )
              }
            />
            <Button
              onClick={() => setSellers(sellers === null ? [] : null)}
              sx={{ mt: 1 }}
              size="small"
              startIcon={
                sellers === null ? <FilterListIcon /> : <AllInclusiveIcon />
              }
            >
              {sellers === null
                ? 'Filter specific sellers'
                : 'Monitor all available sellers'}
            </Button>
          </Box>

          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography fontWeight={500}>
                    Recurring Notifications
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Get notified again if price drops further or after 7 days
                  </Typography>
                </Box>
              }
            />
          </FormControl>
        </DialogContent>

        <Divider />
        <DialogActions
          sx={{
            p: 2.5,
            gap: 1,
            ...(isMobile && {
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
            }),
          }}
        >
          {notification && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          )}
          <Button onClick={() => setOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={sellers?.length === 0 || !targetPrice}
            color="primary"
          >
            {notification ? 'Save' : 'Create alert'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {notification
            ? 'Price alert updated successfully'
            : notified === false
            ? 'Price alert deleted successfully'
            : "Success! 🎉 You've set up a price alert. We'll notify you once the product reaches your target price."}
        </Alert>
      </Snackbar>
    </>
  );
};
export default Notification;
